/**
 * Task 12 — honest receipt toasts for Resend and Collect Payment.
 *
 * The old copy claimed "Receipt sent" the moment the queue accepted an
 * email, and used one warning tone for both a real mix and a total failure
 * (no channel succeeded). Both `ok` fields from the backend mean
 * queued-or-sent, not delivered — only `status === 'sent'` earns the word
 * "emailed" here.
 */
import { toast } from 'sonner';
import type { CollectReceiptDelivery, ReceiptDeliveryResult } from './types';

export interface ReceiptToast {
  tone: 'success' | 'warning' | 'error';
  title: string;
  description?: string;
}

function describeDelivered(d: ReceiptDeliveryResult): string {
  if (d.channel === 'sms') return `Receipt texted to ${d.recipient}`;
  // `ok` on email means queued or sent; only say "emailed" once the provider accepted it.
  return d.status === 'sent'
    ? `Receipt emailed to ${d.recipient}`
    : `Receipt email queued to ${d.recipient}`;
}

function describeFailed(d: ReceiptDeliveryResult): string {
  return `${d.channel === 'email' ? 'Email' : 'Text'}: ${d.error ?? 'unknown error'}`;
}

/** `error` when every channel failed, `warning` only for a real mix. */
export function resendReceiptToast(delivered: readonly ReceiptDeliveryResult[]): ReceiptToast {
  const ok = delivered.filter((d) => d.ok).map(describeDelivered);
  const failed = delivered.filter((d) => !d.ok).map(describeFailed);
  if (ok.length === 0) {
    return failed.length > 0
      ? { tone: 'error', title: 'Receipt not sent', description: failed.join('; ') }
      : { tone: 'error', title: 'Receipt not sent' };
  }
  if (failed.length === 0) return { tone: 'success', title: ok.join('. ') };
  return { tone: 'warning', title: ok.join('. '), description: `Not sent — ${failed.join('; ')}` };
}

/** `title` leads every outcome: "Payment recorded" for cash, "Payment collected" for a card. */
export function collectPaymentToast(
  receiptDelivery: CollectReceiptDelivery | null | undefined,
  title = 'Payment recorded',
): ReceiptToast {
  const sent: string[] = [];
  const failed: string[] = [];
  const email = receiptDelivery?.email;
  if (email) {
    if (email.status === 'queued') sent.push('Receipt email on its way');
    else if (email.status === 'sent') sent.push('Receipt emailed');
    else failed.push(email.errorMessage ?? 'Receipt email not sent');
  }
  const sms = receiptDelivery?.sms;
  if (sms) {
    if (sms.ok) sent.push('Receipt texted');
    else failed.push(sms.error ?? 'Receipt text not sent');
  }
  if (failed.length === 0) {
    return { tone: 'success', title: [title, ...sent].join('. ') };
  }
  if (sent.length === 0) {
    return {
      tone: 'warning',
      title: `${title} — receipt not sent: ${failed.join('; ')}`,
    };
  }
  // A real mix leads with what went out; the failure is the detail.
  return {
    tone: 'warning',
    title: `${title}. ${sent.join('. ')}`,
    description: `Not sent — ${failed.join('; ')}`,
  };
}

export function showReceiptToast({ tone, title, description }: ReceiptToast): void {
  toast[tone](title, description ? { description } : undefined);
}
